import {
  Alert,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { PositioningOutcomeType } from '../utils/sharedTypes'

type PositioningResultProps = {
  outcome: PositioningOutcomeType
}

const formatHours = (hours: number) => `${hours}h`

const formatDuration = (days: number) => {
  if (days < 7) {
    return `approx. ${days} jour${days > 1 ? 's' : ''}`
  }
  const weeks = Math.floor(days / 7)
  return `approx. ${weeks} semaine${weeks > 1 ? 's' : ''}`
}

const PositioningResult = ({ outcome }: PositioningResultProps) => {
  const adjustedParts = outcome.part_adjustments.filter(
    (part) =>
      part.theoretical_hours != null &&
      part.adjusted_hours != null &&
      Math.abs(part.adjusted_hours - part.theoretical_hours) >= 0.1,
  )

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <Alert severity="success">
          Merci, votre test de positionnement est enregistré. Voici comment nous avons personnalisé votre parcours.
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Durée de formation :
          <span style={{ fontWeight: 'normal' }}>
            {outcome.theoretical_duration_hours <= 0 ||
            Math.abs(outcome.effective_duration_hours - outcome.theoretical_duration_hours) < 0.1
              ? ` ${formatHours(outcome.effective_duration_hours)} (durée du programme inchangée)`
              : ` ${formatHours(outcome.effective_duration_hours)} (au lieu de ${formatHours(
                  outcome.theoretical_duration_hours,
                )})`}
          </span>
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Durée de la session : 
          <span style={{ fontWeight: 'normal' }}>
            {outcome.effective_duration_days != null && ` ${formatDuration(outcome.effective_duration_days)}`}
          </span>
        </Typography>
        {adjustedParts.length === 0 && outcome.skills_to_deepen.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Adaptations effectuées par rapport au parcours standard :{' '}
              <span style={{ fontWeight: 'normal' }}>
                votre niveau correspond au parcours standard, aucun module n’a été modifié.
              </span>
            </Typography>
          </Grid>
        )}
      </Grid>

      {outcome.skill_responses.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Vos réponses
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Compétence</TableCell>
                  <TableCell align="right">Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outcome.skill_responses.map((skill) => (
                  <TableRow key={skill.global_id || skill.label}>
                    <TableCell>{skill.positioning_question || skill.label}</TableCell>
                    <TableCell align="right">{skill.score ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      {adjustedParts.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Modules adaptés à votre niveau
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Module</TableCell>
                  <TableCell align="right">Durée initiale</TableCell>
                  <TableCell align="right">Durée ajustée</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adjustedParts.map((part) => (
                  <TableRow key={part.part_global_id || part.title}>
                    <TableCell>{part.title || '—'}</TableCell>
                    <TableCell align="right">{part.theoretical_hours?.toFixed(1)} h</TableCell>
                    <TableCell align="right">{part.adjusted_hours?.toFixed(1)} h</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      {outcome.skills_to_deepen.length > 0 && (
        <Grid item xs={12}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Points à renforcer pendant la formation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Ces compétences feront l’objet d’un approfondissement dans votre parcours.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {outcome.skills_to_deepen.map((skill) => (
                <Typography component="li" variant="body2" key={skill.global_id || skill.label}>
                  {skill.label}
                </Typography>
              ))}
            </Box>
          </Box>
        </Grid>
      )}

      {outcome.positioning_document_url && (
        <Grid item xs={12}>
          <Typography variant="body2">
            <a href={outcome.positioning_document_url} target="_blank" rel="noopener noreferrer">
              Télécharger la synthèse de votre positionnement (PDF)
            </a>
          </Typography>
        </Grid>
      )}
    </Grid>
  )
}

export default PositioningResult
